import { Injectable, inject } from '@angular/core';
import { AuthService } from '../auth.service';
import { getCorrectedDate } from '../../utils/utils';
import { OpAlertLogSearchResult } from '../../model/opAlertLogs/op-alert-log-search-result';
import {
  OpAlertLog,
  OpAlertLogConnection,
} from '../../model/opAlertLogs/op-alert-log';
import { OpAlertLogNotification } from '../../model/opAlertLogs/op-alert-log-notification';
import { dynamicSort } from '../../core/functions/common-functions';
import { OpAlertLogAlarm } from '../../model/opAlertLogs/op-alert-log-alarm';
import { OpAlertLogEntry } from '../../model/opAlertLogs/op-alert-log-entry';
import { DirtyStatus } from '../../model/common/dirty-status';
import { PIValue } from '../../model/operational-monitoring/pi-value';
import {
  AlarmLimit,
  AlarmLimitConnection,
} from 'src/app/model/operational-monitoring/alarm-limit';
import { PiConfigurableElement } from 'src/app/model/operational-monitoring/pi-configurable-element';
import { ConfigurableAttributes } from 'src/app/model/operational-monitoring/configurable-attributes';
import { AlarmLimitHistory } from 'src/app/model/operational-monitoring/alarm-limit-history';

@Injectable({
  providedIn: 'root',
})
export class OpAlertLogsBuilderService {
  private _auth = inject(AuthService);

  buildOpLogQuery(filters: any) {
    const query = {
      opLogId: null,
      state: null,
      county: null,
      township: null,
      createdAt: null,
      opLogStatus: null,
      createdBy: null,
      businessUnitId: null,
      businessUnits: null,
      facilityUid: null,
      facilityName: null,
      facilityTypeName: null,
    };

    if (filters.state) {
      query.state = filters.state.value.trim();
    }
    if (filters.county) {
      query.county = filters.county.value.trim();
    }
    if (filters.township) {
      query.township = filters.township.value.trim();
    }
    if (filters.createdAt) {
      query.createdAt = filters.createdAt.value;
    }
    if (filters.createdBy) {
      query.createdBy = filters.createdBy.value.trim();
    }
    if (filters.opLogId) {
      query.opLogId = filters.opLogId.value.trim();
    }
    if (filters.opLogStatus) {
      query.opLogStatus = filters.opLogStatus.value.trim();
    }
    if (filters.businessUnitId) {
      query.businessUnitId = filters.businessUnitId.value.trim();
    }
    if (filters.facilityUid) {
      query.facilityUid = filters.facilityUid.value.trim();
    }
    if (filters.facilityName) {
      query.facilityName = filters.facilityName.value.trim();
    }
    if (filters.facilityTypeName) {
      query.facilityTypeName = filters.facilityTypeName.value.trim();
    }
    if (filters.businessUnits) {
      query.businessUnits = filters.businessUnits;
    }

    return query;
  }

  buildOpAlertLogSearch(item: any): OpAlertLogSearchResult {
    return {
      id: item.id,
      opLogId: item.opLogId,
      opLogStatus: this.getNameForStatus(item.opLogStatus),
      state: item.state,
      facilityId: item.facilityId,
      facilityName: item.facilityName,
      facilityUid: item.facilityUid,
      facilityTypeName: item.facilityTypeName,
      businessUnitId: item.businessUnitId,
      buName: item.buName,
      county: item.county,
      township: item.township,
      createdAt: getCorrectedDate(item.createdAt)!,
      createdBy: item.createdBy,
      updatedAt: getCorrectedDate(item.updatedAt)!,
      updatedBy: item.updatedBy,
      areaId: item.areaId,
      aor: item.aor,
      aorName: item.aorName,
    };
  }

  buildOpAlertLog(item: any): OpAlertLog {
    const log: OpAlertLog = {
      id: item.id,
      opLogId: item.opLogId,
      BusinessUnit: item.BusinessUnit,
      businessUnitId: item.BusinessUnit.id,
      facilityId: item.Facility.id,
      Facility: item.Facility,
      Contact: item.Contact,
      state: item.state,
      county: item.county,
      township: item.township,
      opLogStatus: item.opLogStatus,
      piPoint: item.piPoint,
      OpAlertLogAlarms: this.buildOpAlertLogAlarms(item.OpAlertLogAlarms),
      OpAlertLogEntries: this.buildOpAlertLogEntries(item.OpAlertLogEntries),
      OpAlertLogNotifications: this.buildOpAlertLogNotifications(
        item.OpAlertLogNotifications
      ),
      createdAt: getCorrectedDate(item.createdAt)!,
      updatedAt: getCorrectedDate(item.updatedAt)!,
      createdBy: item.createdBy,
    };

    return log;
  }

  buildOpAlertLogEntry(item: any): OpAlertLogEntry {
    return {
      id: item.id,
      entry: item.entry,
      time: getCorrectedDate(item.time)!,
      user: item.user,
      createdBy: item.createdBy,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      deletedAt: item.deletedAt,
      note: item.entry,
    };
  }

  buildOpAlertLogEntries(item: any[]): OpAlertLogEntry[] {
    const retVal: OpAlertLogEntry[] = [];

    if (!item) {
      return retVal;
    }

    item.forEach((e) => {
      const entry: OpAlertLogEntry = this.buildOpAlertLogEntry(e);
      retVal.push(entry);
    });

    return retVal.sort(dynamicSort('time', 1));
  }

  buildOpAlertLogAlarms(item: any[]): OpAlertLogAlarm[] {
    const retVal: OpAlertLogAlarm[] = [];

    if (!item) {
      return retVal;
    }

    item.forEach((e) => {
      const entry: OpAlertLogAlarm = {
        id: e.id,
        alarm: e.alarm,
        time: getCorrectedDate(e.time)!,
        user: e.user,
        createdBy: e.createdBy,
        createdAt: e.createdAt,
        updatedAt: e.updatedAt,
        deletedAt: e.deletedAt,
        note: e.alarm,
      };
      retVal.push(entry);
    });

    return retVal.sort(dynamicSort('time', 1));
  }

  buildOpAlertLogNotifications(item: any[]): OpAlertLogNotification[] {
    const retVal: OpAlertLogNotification[] = [];

    if (!item) {
      return retVal;
    }

    item.forEach((e) => {
      const entry: OpAlertLogNotification = {
        id: e.id,
        opAlertLogId: e.opAlertLogId,
        note: e.note,
        time: getCorrectedDate(e.time)!,
        user: e.user,
      };
      retVal.push(entry);
    });

    return retVal.sort(dynamicSort('time', -1));
  }

  buildUpdateStatement(
    opAlertLog: OpAlertLog,
    changes: any,
    markAsDeleted: Boolean
  ) {
    const retVal: any = {
      id: opAlertLog.id,
      dirtyStatus: DirtyStatus.UPDATED,
      opLogStatus: this.getField('opLogStatus', opAlertLog, changes),
      opLogId: opAlertLog.opLogId,
      state: opAlertLog.state,
      county: opAlertLog.county,
      township: opAlertLog.township,
      updatedAt: opAlertLog.updatedAt,
      updatedBy: opAlertLog.updatedBy,
      createdAt: opAlertLog.createdAt,
      createdBy: opAlertLog.createdBy,
      markAsDeleted: markAsDeleted,
      businessUnitId: opAlertLog.BusinessUnit.id,
      facilityId: opAlertLog.Facility.id,
    };

    if (changes.OpAlertLog) {
      if (changes.OpAlertLog.OpAlertLogLocation) {
        if (
          changes.OpAlertLog.OpAlertLogLocation.county ||
          changes.OpAlertLog.OpAlertLogLocation.county === ''
        ) {
          retVal.county = changes.OpAlertLog.OpAlertLogLocation.county.county;
        }

        if (
          changes.OpAlertLog.OpAlertLogLocation.state ||
          changes.OpAlertLog.OpAlertLogLocation.state === ''
        ) {
          retVal.state = changes.OpAlertLog.OpAlertLogLocation.state;
        }

        if (
          changes.OpAlertLog.OpAlertLogLocation.township ||
          changes.OpAlertLog.OpAlertLogLocation.township === ''
        ) {
          retVal.township =
            changes.OpAlertLog.OpAlertLogLocation.township.township;
        }
      }

      if (changes.OpAlertLog.OpAlertLogFacility) {
        if (changes.OpAlertLog.OpAlertLogFacility.facilityId) {
          retVal.facilityId = changes.OpAlertLog.OpAlertLogFacility.facilityId;
        }
      }
    }

    if (changes.businessUnit) {
      retVal['businessUnit'] = changes.businessUnit.id;
    }

    if (changes.facility) {
      retVal['facility'] = changes.facility.id;
    }

    if (opAlertLog.OpAlertLogEntries.some((e) => e.dirtyStatus! > 0)) {
      retVal['OpAlertLogEntry'] = opAlertLog.OpAlertLogEntries.find(
        (e) => e.dirtyStatus! > 0
      );
    }

    return retVal;
  }

  buildNewOpAlertLogEntry(raw: any, opLog: OpAlertLog): OpAlertLogEntry {
    const retVal: OpAlertLogEntry = {
      id: '',
      opAlertLogId: opLog.id,
      dirtyStatus: DirtyStatus.NEW,
      time: raw.entryTime ? raw.entryTime : new Date(),
      createdBy: this._auth.getUserName(),
      createdAt: raw.entryTime ? raw.entryTime : new Date(),
      updatedAt: raw.entryTime ? raw.entryTime : new Date(),
      user: this._auth.getUserName(),
      markAsDeleted: false,
      entry: raw.statusUpdate,
      note: raw.statusUpdate,
    };

    return retVal;
  }

  getNameForStatus(status: string) {
    if (!status) {
      return 'N/A';
    } else if (status === 'N') {
      return 'NEW';
    } else if (status === 'A') {
      return 'ACKNOWLEDGED';
    } else {
      return status;
    }
  }

  getField(field: string, uiObj: any, input: any): any {
    if (field in input && input[field] !== undefined) {
      return input[field];
    } else if (field in uiObj && uiObj[field] !== undefined) {
      return uiObj[field];
    } else {
      return null;
    }
  }

  buildPIValue(item: any): PIValue {
    return {
      timestamp: new Date(item.timestamp),
      unitsAbbreviation: item.unitsAbbreviation,
      value: parseFloat(item.value),
      good: item.good,
      questionable: item.questionable,
      substituted: item.substituted,
      annotated: item.annotated,
    };
  }

  buildAlarmLimit(item: any): AlarmLimit {
    return {
      id: item.id,
      facilityId: item.facilityId,
      facilityName: item.Facility?.facilityName,
      Facility: item.Facility,
      alarmAttributeName: item.alarmAttributeName,
      assetIdentifier: item.assetIdentifier,
      effectiveDateTime: getCorrectedDate(item.effectiveDateTime)!,
      value: item.value,
      updater: item.updater,
      updatedAt: getCorrectedDate(item.updatedAt)!,
      updateReason: item.updateReason,
      datatype: item.datatype,
    };
  }

  buildAlarmLimitConnection(item: any): AlarmLimitConnection {
    return {
      items: item.items.map((i: any) => this.buildAlarmLimit(i)),
      totalRecords: item.totalRecords,
    };
  }

  buildConfigurableAttributes(item: any): ConfigurableAttributes {
    return {
      name: item.name,
      datatype: item.datatype,
      children: item.children.map((i: any) =>
        this.buildConfigurableAttributes(i)
      ),
    };
  }

  buildPiConfigurableElement(item: any): PiConfigurableElement {
    return {
      name: item.name,
      assetIdentifier: item.assetIdentifier,
      facilityUid: item.facilityUid,
      configurableAttributes: item.configurableAttributes.map((i: any) =>
        this.buildConfigurableAttributes(i)
      ),
      alarmAttributeName: item.alarmAttributeName,
      datatype: item.datatype,
      children: item.children.map((i: any) =>
        this.buildConfigurableAttributes(i)
      ),
      webId: item.webId,
      synced: item.synced,
    };
  }

  buildAlarmLimitHistory(item: any): AlarmLimitHistory {
    return {
      timestamp: getCorrectedDate(item.timestamp)!,
      id: item.id,
      updater: item.updater,
      value: item.value,
      effectiveDateTime: getCorrectedDate(item.effectiveDateTime)!,
      updateReason: item.updateReason,
    };
  }

  buildOpAlertLogConnection(item: any): OpAlertLogConnection {
    return {
      items: item.items.map((i: any) => this.buildOpAlertLogSearch(i)),
      totalRecords: item.totalRecords,
    };
  }
}
